import type { ComputedRef, Ref } from 'vue';

import { computed, ref } from 'vue';

import { HANDLE_DRAG_TAP_THRESHOLD_PX } from './constants';
import { SheetLevel } from './types';

// Estado do drawer com uniao discriminada.
// Objetivo: evitar combinacoes invalidas de flags.
export type SheetPhase =
	| { kind: 'idle' }
	| {
			kind: 'dragging';
			startY: number;
			startScrollY: number;
			startLevel: SheetLevel;
			distance: number;
			pendingTargetY: number;
	  }
	| { kind: 'snapping'; from: number; to: number };

export interface SheetMachine {
	phase: Ref<SheetPhase>;
	isDragging: ComputedRef<boolean>;
	isTransitioning: ComputedRef<boolean>;
	wasDragGesture: ComputedRef<boolean>;
	beginDrag(opts: {
		startY: number;
		startScrollY: number;
		startLevel: SheetLevel;
	}): void;
	updateDrag(dy: number, pendingTargetY: number): void;
	endDrag(): void;
	beginSnap(from: number, to: number): void;
	endSnap(): void;
}

export function useSheetMachine(): SheetMachine {
	const phase = ref<SheetPhase>({ kind: 'idle' });

	const isDragging = computed(() => phase.value.kind === 'dragging');
	const isTransitioning = computed(() => phase.value.kind === 'snapping');

	// True quando o gesto passou do limiar de "tap".
	const wasDragGesture = computed(() => {
		const p = phase.value;
		return p.kind === 'dragging' && p.distance >= HANDLE_DRAG_TAP_THRESHOLD_PX;
	});

	// Inicia uma sessao de drag.
	function beginDrag(opts: {
		startY: number;
		startScrollY: number;
		startLevel: SheetLevel;
	}): void {
		phase.value = {
			kind: 'dragging',
			startY: opts.startY,
			startScrollY: opts.startScrollY,
			startLevel: opts.startLevel,
			distance: 0,
			pendingTargetY: opts.startScrollY
		};
	}

	// Atualiza distancia e alvo durante o drag.
	function updateDrag(dy: number, pendingTargetY: number): void {
		const p = phase.value;
		if (p.kind !== 'dragging') return;
		// Mantem mutacao in-place para reduzir custo por frame.
		p.distance = Math.max(p.distance, Math.abs(dy));
		p.pendingTargetY = pendingTargetY;
	}

	// Finaliza drag e volta para idle.
	function endDrag(): void {
		phase.value = { kind: 'idle' };
	}

	// Marca inicio de snap programatico.
	function beginSnap(from: number, to: number): void {
		phase.value = { kind: 'snapping', from, to };
	}

	// Fecha snap se ainda estiver em snapping.
	function endSnap(): void {
		if (phase.value.kind === 'snapping') {
			phase.value = { kind: 'idle' };
		}
	}

	return {
		phase,
		isDragging,
		isTransitioning,
		wasDragGesture,
		beginDrag,
		updateDrag,
		endDrag,
		beginSnap,
		endSnap
	};
}
